import {
  useCallback, useEffect, useRef, useState, type CSSProperties, type JSX, type ReactNode,
} from 'react'
import {
  api, ApiError,
  type EndUse, type EstimateFigures, type ScenarioOverrides, type ScenarioPayload,
} from '../api.js'
import { formatKw, formatMoney, grouped, KwFigure } from './Pill.js'
import { T } from '../tokens.js'

/**
 * The what-if panel — R-17, and the half of display rule 2 that a rep can
 * operate.
 *
 * ## Every figure in here came off the wire
 *
 * The mock computes the adjusted profile **in the browser**, from an invented
 * `exclFactor` (`refrigeration +0.10`, `lighting +0.08`, `cooling` from the
 * decomposition share, capped at 0.85). Those numbers exist nowhere in the
 * engine and nowhere in the configuration document; they were drawn to make a
 * picture move. A what-if a rep quotes to a customer has to be reproducible
 * from the stamps on it, and a number the browser invented is reproducible from
 * nothing.
 *
 * So every control here sends `POST /api/accounts/:id/scenarios` and this
 * component renders what comes back. It computes exactly one class of value:
 * the **difference** between two figures the server sent, both of which are
 * printed beside it (`Record 227.2 kW · −47.6 kW`). That is a comparison
 * between two published figures rather than an estimate, and a panel whose
 * whole purpose is comparison that made a rep do the subtraction on paper would
 * get the subtraction done anyway, unchecked.
 *
 * ## The mock's duration helper text is false and is not shipped
 *
 * The mock says *"Fewer hours to sustain moves the binding hour earlier, where
 * there is more headroom."* `curtailment.ts` anchors the examined span at the
 * window **close** (`theoreticalSpanStart = endHour - spanLength`), so a
 * shorter event starts *later* and drops the **earliest** hours — which on a
 * profile that decays through the evening are the highest ones. The mock's own
 * `bindIndex` scans forward from the window *start*; the two are opposite
 * models and the engine's is the one that produced the number on the screen.
 *
 * Measured on Desert Ridge: the binding hour is 21:00 and the nomination
 * 227.2 kW at durations 2, 3, 4 **and** 5. What a shorter duration moves is the
 * span (`spanStartHour` 18 → 19) and the season value (`$14,446.30` →
 * `$13,852.94`, all of it energy). What moves the kW is excluding an end use:
 * withholding refrigeration takes Desert Ridge from 227.2 kW to 179.6 kW.
 *
 * ## The scenario's confidence label is pre-cohort, so this panel does not draw one
 *
 * `estimates.confidence` on the record is written **after** a run completes:
 * `assignCohorts` compares each account against its industry-and-size cohort
 * and steps outliers one rung down. All 26 Medium accounts on the seeded book
 * are cohort step-downs. A scenario belongs to no run, has no cohort, and is
 * scored fresh — so its label is the engine's own, pre-cohort one, and the
 * envelope says so in `confidenceBasis`.
 *
 * Measured: `acct-fill-0035` is `medium` as its estimate of record and `high`
 * as a scenario **with no overrides at all**. Printing that label beside the
 * record's would show every Medium account on the book improving the instant a
 * rep opened a what-if — an artefact of which pass computed the label, drawn as
 * an effect of the rep's question.
 *
 * The consequence figures therefore carry the **record's** label, named through
 * `aria-describedby` (display rule 1), and this panel says in words why there
 * is no second one. The basis is in the markup as `data-confidence-basis` so
 * that the field is load-bearing rather than decorative.
 */

/** Mandated copy, from the mock, transcribed. */
export const DURATION_LABEL = 'Event duration the customer will accept'
export const EXCLUDE_LABEL = 'End uses the customer says are non-negotiable'
export const CONSEQUENCE_LABEL = 'Consequence'

/**
 * The corrected duration helper. **Not the mock's sentence** — see the module
 * docblock. This one describes the engine that produced the figure beside it.
 */
export const DURATION_HELP =
  'A shorter event is measured against the last hours of the window, so it drops the earlier, '
  + 'higher hours rather than the later ones.'

/** Why the consequence block shows no confidence label of its own. */
export const CONFIDENCE_SUPPRESSED =
  'Confidence stays the estimate of record’s. A scenario is scored on its own and never goes '
  + 'through the cohort comparison that sets the label, so a label computed here would not be '
  + 'comparable with the one above.'

export const NOMINATION_LABEL = 'Nomination the customer will commit to'

/**
 * The nomination override's helper, and the second half of it is the point.
 *
 * A hand-entered figure did not come from any hour, so nothing on this screen
 * may say it binds at one — Task 14 withheld `BINDS` from the chart for exactly
 * this and Task 15 rewrote the drill-down caption. This panel is where the
 * first `overridden` estimate in the application is actually created, so it is
 * also where a rep is told what changes.
 */
export const NOMINATION_HELP =
  'Leave this blank to use the figure the profile supports. A figure entered here was not read '
  + 'off any hour, so nothing will say it binds at one.'

export const AT_RECORD_CAPTION = 'This is the estimate of record.'

/** What a failed scenario says. The route's own message when it sent one. */
export const SCENARIO_FAILED = 'That what-if could not be computed. Change something and try again.'

/** The four durations the mock's segmented control offers. */
export const DURATION_OPTIONS: readonly number[] = [2, 3, 4, 5]

/**
 * The three end uses the mock lets a customer declare non-negotiable, in its
 * order. The engine recognises seven; these are the three a rep hears about on
 * a call, and `EndUse` keeps the other four one type-check away rather than one
 * 400 away.
 */
export const EXCLUDABLE: readonly { id: EndUse; label: string }[] = [
  { id: 'refrigeration', label: 'Refrigeration' },
  { id: 'cooling', label: 'Cooling' },
  { id: 'lighting', label: 'Lighting' },
]

/**
 * A label for **every** end use the engine recognises, not only the three above.
 *
 * `EXCLUDABLE` is a shortlist for the panel's standing controls. It is not the
 * contract, and reading it as one is the defect this map closes:
 * `POST /api/accounts/:id/scenarios` validates `excludedEndUses` against
 * `END_USES` itself and accepts all seven, `computeProfile` honours all seven,
 * and Task 5's `suggestedOverrideFor` therefore offers all seven — a rejected
 * `process` or `ventilation` strategy has a real, applicable exclusion.
 *
 * Without this map `labelOf` fell back to the raw union member, and the app bar
 * read "process withheld" for a scenario a rep would describe as "process load
 * withheld". A lower-cased identifier in a sentence a rep reads to a customer
 * is a small thing that looks exactly like a bug.
 */
const END_USE_LABELS: Record<EndUse, string> = {
  refrigeration: 'Refrigeration',
  cooling: 'Cooling',
  lighting: 'Lighting',
  ventilation: 'Ventilation',
  process: 'Process load',
  plug: 'Plug load',
  other: 'Other load',
}

/**
 * The checkboxes to draw: the shortlist, **plus a control for anything actually
 * excluded that the shortlist does not cover.**
 *
 * This is the decision Task 12's rejection flow forces, and there were three
 * options. Applying an out-of-shortlist override directly, without a control,
 * gives a scenario the rep cannot undo from the panel that produced it. Growing
 * `EXCLUDABLE` to all seven throws away a design decision transcribed from the
 * mock — those three are the ones a rep hears about on a call, and a
 * seven-checkbox column is a different panel. This is the third: the shortlist
 * stays the default, and the panel grows exactly the controls the draft needs.
 *
 * So a suggestion is never silently dropped and never lands as a control that
 * does nothing — the seeded end use arrives with its own checkbox, ticked, and
 * unticking it is how the rep takes it back.
 *
 * **The grown controls only ever grow**, which is why the caller passes a
 * separate list rather than `draft.excluded`. A checkbox derived directly from
 * the draft would delete itself the moment the rep unticked it, so the act of
 * taking an exclusion back would also take away the control for putting it
 * back — a disappearing checkbox reads as a bug, and a rep who unticked by
 * accident would have no way to undo it.
 */
function optionsFor(grown: readonly EndUse[]): { id: EndUse; label: string }[] {
  return [...EXCLUDABLE, ...extrasOf(grown).map((endUse) => ({ id: endUse, label: END_USE_LABELS[endUse] }))]
}

/** The end uses in `list` that the shortlist does not already offer, in order. */
function extrasOf(list: readonly EndUse[]): EndUse[] {
  return list.filter((endUse) => !EXCLUDABLE.some((option) => option.id === endUse))
}

const microLabel: CSSProperties = {
  fontSize: T.micro.size,
  fontWeight: T.weight.semi,
  letterSpacing: T.micro.tracking,
  textTransform: T.micro.transform,
  color: T.color.micro,
}

/** What the rep has asked for. `durationHours` is the *selected* segment. */
export interface Draft {
  durationHours: number
  excluded: EndUse[]
  /** `null` is "read it off the profile", which is what the record does. */
  nominationKw: number | null
}

/**
 * The duration the estimate of record was measured over, from its own span.
 *
 * **Inferred, and the inference has one edge.** `config.events
 * .assumedDurationHours` is the real answer and no route serves it to a screen
 * (Task 15 ruled that the drill-down payload takes no further configuration,
 * and the config *derivation* would cost a second request for one integer).
 * `spanEndHour - spanStartHour` is that duration clamped to the event window,
 * so a program configured for an event longer than its own window would show
 * the window's length selected here. On the shipped configuration — 4-hour
 * events in a 5-hour window — they agree, and `curtailment.ts` is what makes
 * the clamp true rather than an assumption. Recorded in task-17-report.md.
 */
export function recordDurationHours(record: EstimateFigures): number {
  const { spanStartHour, spanEndHour } = record.result.nomination
  return spanEndHour - spanStartHour
}

/** A draft that changes nothing is not a scenario, and must not be sent as one. */
export function isAtRecord(draft: Draft, record: EstimateFigures): boolean {
  return draft.durationHours === recordDurationHours(record)
    && draft.excluded.length === 0
    && draft.nominationKw === null
}

/**
 * The draft as the route's `Overrides`.
 *
 * Built key by key rather than spread, matching the route's own reading of the
 * same five keys: `exactOptionalPropertyTypes` makes an explicit `undefined` a
 * different thing from an absent key, and an override equal to the record's is
 * omitted so that "back where we started" is a state this component can see.
 */
export function overridesOf(draft: Draft, record: EstimateFigures): ScenarioOverrides {
  const overrides: ScenarioOverrides = {}
  if (draft.durationHours !== recordDurationHours(record)) {
    overrides.eventDurationHours = draft.durationHours
  }
  if (draft.excluded.length > 0) overrides.excludedEndUses = [...draft.excluded]
  if (draft.nominationKw !== null) overrides.nominationKw = draft.nominationKw
  return overrides
}

const labelOf = (endUse: EndUse): string => END_USE_LABELS[endUse]

/**
 * The rep's label for the question, built from the question.
 *
 * The route requires a non-empty name and echoes it into every payload built
 * from the answer, including the one-pager's, so that a scenario can never be
 * shown without being named as one (display rule 2). The mock has no name
 * field; describing what was asked is better than `Scenario 1`, and it is what
 * ends up in the app bar.
 */
export function scenarioNameFor(overrides: ScenarioOverrides): string {
  const parts: string[] = []
  if (overrides.eventDurationHours !== undefined) {
    parts.push(`${grouped(overrides.eventDurationHours, 0)}-hour event`)
  }
  if (overrides.excludedEndUses !== undefined && overrides.excludedEndUses.length > 0) {
    parts.push(`${overrides.excludedEndUses.map(labelOf).join(' and ')} withheld`)
  }
  if (overrides.nominationKw !== undefined) {
    parts.push(`nomination held at ${formatKw(overrides.nominationKw)}`)
  }
  return parts.length === 0 ? 'No change' : parts.join(' · ')
}

/**
 * What changed and what it cost, for the band across the top of the page.
 *
 * Built from the envelope's **echoed** `overrides` rather than from the panel's
 * draft: the route reports back the overrides it validated and applied, so a
 * key it silently ignored could not be described here as though it had taken
 * effect. Both kW figures are the server's.
 */
export function scenarioSummary(payload: ScenarioPayload, record: EstimateFigures): string {
  const changed = scenarioNameFor(payload.scenario.overrides)
  const nomination = formatKw(payload.estimate.result.nomination.kw)
  return `${changed} · nomination ${nomination} against record ${formatKw(record.result.nomination.kw)}`
}

/** `+47.6 kW` / `−47.6 kW` / `no change`, against a figure printed beside it. */
function deltaOf(value: number, base: number, render: (n: number) => string): string {
  const difference = value - base
  const rendered = render(Math.abs(difference))
  // Compared after rounding to the printed precision, so a difference the panel
  // cannot show does not get a sign put in front of it.
  if (rendered === render(0)) return 'no change'
  return `${difference > 0 ? '+' : '−'}${rendered}`
}

export interface WhatIfProps {
  accountId: string
  /**
   * The estimate of record. It is what every consequence figure is a comparison
   * against, and it never changes while this panel is open — the screen keeps
   * it in state precisely so that reverting cannot land on a different run.
   */
  record: EstimateFigures
  /** The scenario currently on the page, or `null`. Owned by the screen. */
  scenario: ScenarioPayload | null
  /**
   * Told the whole payload, or `null` the moment the controls return to the
   * record's own configuration. The plan's interface is
   * `onScenario={(result | null) => void}`; the screen needs the envelope as
   * well as the figures, so the whole payload goes up.
   */
  onScenario: (scenario: ScenarioPayload | null) => void
  /**
   * The element carrying the **record's** confidence label. Display rule 1: the
   * consequence figures are kW quantities and must name a label, and the label
   * they name is deliberately not one computed for the scenario.
   */
  confidenceLabelId: string
  /**
   * End uses this panel opens with already ticked — R-33's rejection offer
   * (`StrategyList.tsx`), and **read exactly once, at mount**.
   *
   * Seeding is not applying. It pre-selects the checkbox and fires no request:
   * the panel opens showing the estimate of record with the box ticked, and the
   * rep applies the exclusion through the scenario path, which is what "the
   * rejection offers the exclusion" means. `run` is called only from `apply`,
   * and nothing in the mount path calls `apply`.
   *
   * **It cannot reseed a panel that is already open**, and that is the point:
   * this is `useState`'s initial value, so a rep who has a duration and a
   * nomination in flight keeps them. What the screen does with an offer that
   * arrives against an open panel is `AccountDetail.tsx`'s decision, and it is
   * `addExclusions` below rather than a remount.
   */
  initialExcluded?: readonly EndUse[] | undefined
  /**
   * An addition to an **already-open** draft, applied when `token` changes.
   *
   * The alternative was to remount the panel with a new `initialExcluded`,
   * which discards whatever the rep had set up — a rep mid-conversation with
   * "5 hr" and a hand-entered nomination on screen loses both to a click they
   * made about something else. So an offer against an open panel is not
   * delivered at all until the rep accepts it, and when they do it is merged
   * into the draft they already have.
   *
   * The token is what makes "accept the same offer twice" a real event and
   * "re-render for any other reason" not one; the merged draft is idempotent in
   * the end uses either way. Accepting **does** run the scenario, because
   * accepting is the rep applying it — the click is the deliberate act R-33
   * requires, not a side effect of the rejection.
   */
  addExclusions?: { token: number; endUses: readonly EndUse[] } | undefined
}

export function WhatIf({
  accountId, record, scenario, onScenario, confidenceLabelId,
  initialExcluded, addExclusions,
}: WhatIfProps): JSX.Element {
  const [draft, setDraft] = useState<Draft>(() => ({
    durationHours: recordDurationHours(record),
    excluded: [...(initialExcluded ?? [])],
    nominationKw: null,
  }))
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)
  /**
   * End uses this panel has grown a checkbox for beyond `EXCLUDABLE`'s three.
   *
   * It only ever grows, for the reason `optionsFor` gives. Seeded from
   * `initialExcluded` so an offer that opens the panel arrives with its control
   * already there, and extended by `addExclusions` below.
   */
  const [grown, setGrown] = useState<EndUse[]>(() => extrasOf(initialExcluded ?? []))

  /**
   * The draft as of *now*, not as of the render that produced the handler.
   *
   * Task 12's stale-props defect, which no test caught: switching three chips
   * off from the console switched off one, because every handler computed from
   * the state of its own render. A human cannot click twice in one tick and
   * `userEvent` re-renders between clicks, so the suite was blind to it. The
   * ref is read and written synchronously inside `apply`, so three toggles in
   * one tick compose. It is not a `setState` updater because a fetch is issued
   * from here and React does not promise to call an updater exactly once.
   */
  const draftRef = useRef<Draft>(draft)

  /**
   * Which request is the current question. A rep clicking `2 hr` then `5 hr`
   * has two requests in flight, and the slower one is not the answer to what
   * they are looking at — an out-of-order landing would put a figure on the
   * page that no control on it selects.
   */
  const sequence = useRef(0)
  const alive = useRef(true)

  useEffect(() => {
    alive.current = true
    return () => { alive.current = false }
  }, [])

  const run = useCallback((next: Draft): void => {
    // Back at the record: no request at all. Asking the server to recompute an
    // unchanged question would put the band, the red series and the pre-cohort
    // label on screen for a scenario that changed nothing — which is exactly
    // the `acct-fill-0035` case (`medium` of record, `high` as a no-op).
    if (isAtRecord(next, record)) {
      sequence.current += 1
      setBusy(false)
      setFailure(null)
      onScenario(null)
      return
    }

    const overrides = overridesOf(next, record)
    const era = sequence.current + 1
    sequence.current = era
    setBusy(true)
    setFailure(null)
    void api.scenario(accountId, { name: scenarioNameFor(overrides), overrides })
      .then((payload) => {
        if (!alive.current || sequence.current !== era) return
        setBusy(false)
        onScenario(payload)
      })
      .catch((error: unknown) => {
        if (!alive.current || sequence.current !== era) return
        setBusy(false)
        // The route's own message when it sent one: it describes the request
        // the caller made and never anything about the book behind it, and
        // "eventDurationHours: 7 is outside the 2-5 hour range this program's
        // events run for" is the answer, not a detail.
        setFailure(error instanceof ApiError ? error.detail ?? error.message : SCENARIO_FAILED)
      })
  }, [accountId, onScenario, record])

  const apply = useCallback((change: (previous: Draft) => Draft): void => {
    const next = change(draftRef.current)
    draftRef.current = next
    setDraft(next)
    run(next)
  }, [run])

  /**
   * The token this panel has already honoured.
   *
   * Initialised from the mount's own `addExclusions` rather than from nothing,
   * because the screen hands the same offer to `initialExcluded` and to this
   * prop when it opens a closed panel: the draft is already seeded and firing
   * the effect as well would send a scenario request the rep never asked for,
   * turning "offered" into "applied" at mount. Every later change of the token
   * is a rep clicking accept.
   */
  const honoured = useRef<number | null>(addExclusions?.token ?? null)

  useEffect(() => {
    if (addExclusions === undefined || addExclusions.token === honoured.current) return
    honoured.current = addExclusions.token
    setGrown((previous) => [
      ...previous,
      ...extrasOf(addExclusions.endUses).filter((endUse) => !previous.includes(endUse)),
    ])
    apply((previous) => ({
      ...previous,
      excluded: [
        ...previous.excluded,
        ...addExclusions.endUses.filter((endUse) => !previous.excluded.includes(endUse)),
      ],
    }))
  }, [addExclusions, apply])

  const shown = scenario === null ? record : scenario.estimate
  const isScenario = scenario !== null

  return (
    <section
      data-testid="whatif"
      data-busy={busy ? 'true' : 'false'}
      aria-label="What-if"
      style={{
        background: '#fff',
        borderBottom: `1.5px solid ${T.color.rule}`,
        borderTop: `1.5px solid ${T.color.rule}`,
        padding: '16px 24px',
      }}
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 28, alignItems: 'start' }}>
        <div>
          <div style={microLabel} id="whatif-duration">{DURATION_LABEL}</div>
          <div
            role="group"
            aria-labelledby="whatif-duration"
            style={{ display: 'flex', marginTop: 10, border: `1.5px solid ${T.color.rule}`, width: 'fit-content' }}
          >
            {DURATION_OPTIONS.map((hours) => {
              const on = draft.durationHours === hours
              return (
                <button
                  key={hours}
                  type="button"
                  aria-pressed={on}
                  data-duration={hours}
                  onClick={() => { apply((previous) => ({ ...previous, durationHours: hours })) }}
                  style={{
                    padding: '9px 14px',
                    fontFamily: T.fontFamily,
                    fontSize: 10.5,
                    fontWeight: T.weight.bold,
                    letterSpacing: '0.07em',
                    textTransform: T.micro.transform,
                    border: 0,
                    borderRadius: T.radius.square,
                    cursor: 'pointer',
                    background: on ? T.color.navy : '#fff',
                    color: on ? '#fff' : T.color.body,
                  }}
                >
                  {`${hours} hr`}
                </button>
              )
            })}
          </div>
          <div style={{ fontSize: 11.5, color: T.color.muted, marginTop: 9, maxWidth: '40ch' }}>
            {DURATION_HELP}
          </div>
        </div>

        <div>
          <div style={microLabel} id="whatif-end-uses">{EXCLUDE_LABEL}</div>
          <div
            role="group"
            aria-labelledby="whatif-end-uses"
            style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 }}
          >
            {optionsFor(grown).map((option) => {
              const on = draft.excluded.includes(option.id)
              return (
                <label
                  key={option.id}
                  style={{ display: 'flex', gap: 9, alignItems: 'center', fontSize: 13, cursor: 'pointer' }}
                >
                  <input
                    type="checkbox"
                    checked={on}
                    data-end-use={option.id}
                    onChange={() => {
                      apply((previous) => ({
                        ...previous,
                        excluded: previous.excluded.includes(option.id)
                          ? previous.excluded.filter((endUse) => endUse !== option.id)
                          : [...previous.excluded, option.id],
                      }))
                    }}
                    style={{ width: 15, height: 15, accentColor: T.color.red }}
                  />
                  <span>{option.label}</span>
                </label>
              )
            })}
          </div>
        </div>

        <div>
          <div style={microLabel}>{CONSEQUENCE_LABEL}</div>
          <div
            data-testid="consequence"
            data-scenario={isScenario ? 'true' : 'false'}
            /*
              The basis is in the markup, not only in a comment, because it is
              the reason there is no confidence pill in this block. A future
              payload whose label had been through the cohort pass would make
              this attribute say so, and the copy below would be the thing to
              revisit.
            */
            data-confidence-basis={scenario?.scenario.confidenceBasis ?? ''}
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              marginTop: 10,
              border: `1.5px solid ${T.color.rule}`,
            }}
          >
            <Consequence label="Nomination" testId="consequence-nomination" border>
              <KwFigure
                kw={shown.result.nomination.kw}
                confidence={record.confidence}
                label="adjacent"
                confidenceLabelId={confidenceLabelId}
                tone={isScenario ? 'red' : 'ink'}
                testId="consequence-nomination-kw"
              />
              <Caption testId="consequence-nomination-delta">
                {isScenario
                  ? `Record ${formatKw(record.result.nomination.kw)} · `
                    + `${deltaOf(shown.result.nomination.kw, record.result.nomination.kw, formatKw)}`
                  : AT_RECORD_CAPTION}
              </Caption>
            </Consequence>

            <Consequence label="Season value" testId="consequence-value">
              <span
                data-testid="consequence-value-total"
                style={{
                  fontSize: 22,
                  fontWeight: T.weight.bold,
                  fontVariantNumeric: 'tabular-nums',
                  color: isScenario ? T.color.red : T.color.money,
                }}
              >
                {formatMoney(shown.result.value.seasonTotal)}
              </span>
              <Caption testId="consequence-value-delta">
                {isScenario
                  ? `Record ${formatMoney(record.result.value.seasonTotal)} · `
                    + `${deltaOf(shown.result.value.seasonTotal, record.result.value.seasonTotal, formatMoney)}`
                  : AT_RECORD_CAPTION}
              </Caption>
            </Consequence>
          </div>
          <p
            data-testid="confidence-basis-note"
            style={{ fontSize: 11.5, color: T.color.muted, margin: '9px 0 0', maxWidth: '44ch' }}
          >
            {CONFIDENCE_SUPPRESSED}
          </p>
        </div>
      </div>

      {/*
        The fourth control, and the mock has none: `nominationKw` is one of the
        five overrides the route takes, and without it nothing in this
        application can ever produce an estimate with `nomination.overridden`
        set — which is the state Task 14's chart and Task 15's caption were
        both built to handle.

        It commits on blur and on Enter rather than on every keystroke: a
        request per character would ask the server four questions on the way to
        `150` and answer the rep with the third one.
      */}
      <div style={{ borderTop: `1px solid ${T.color.hairline}`, marginTop: 16, paddingTop: 14 }}>
        <label style={{ ...microLabel, display: 'block' }} htmlFor="whatif-nomination">
          {NOMINATION_LABEL}
        </label>
        <input
          id="whatif-nomination"
          type="number"
          min={0}
          step="0.1"
          inputMode="decimal"
          data-testid="nomination-override"
          defaultValue=""
          onBlur={(event) => { commitNomination(event.currentTarget.value, apply) }}
          onKeyDown={(event) => {
            if (event.key !== 'Enter') return
            event.preventDefault()
            commitNomination(event.currentTarget.value, apply)
          }}
          style={{
            marginTop: 8,
            width: 160,
            padding: '9px 11px',
            fontFamily: T.fontFamily,
            fontSize: 13,
            border: `1.5px solid ${T.color.border}`,
            borderRadius: T.radius.square,
            background: '#fff',
            color: T.color.ink,
          }}
        />
        <div style={{ fontSize: 11.5, color: T.color.muted, marginTop: 8, maxWidth: '60ch' }}>
          {NOMINATION_HELP}
        </div>
      </div>

      {failure === null
        ? null
        : (
          <p
            data-testid="whatif-error"
            role="alert"
            style={{
              margin: '14px 0 0',
              padding: '10px 12px',
              background: T.tint.red,
              border: `1px solid ${T.color.red}`,
              fontSize: 12.5,
              color: T.color.declinedInk,
            }}
          >
            {failure}
          </p>
        )}
    </section>
  )
}

/**
 * A blank field is "read it off the profile" and any number is an override.
 *
 * Parsed here rather than in the handler so both commit paths — blur and Enter
 * — read the same rule. A field that is not a number at all is left alone
 * rather than being turned into a 0 kW commitment.
 */
function commitNomination(raw: string, apply: (change: (previous: Draft) => Draft) => void): void {
  const trimmed = raw.trim()
  if (trimmed === '') {
    apply((previous) => (previous.nominationKw === null ? previous : { ...previous, nominationKw: null }))
    return
  }
  const value = Number(trimmed)
  if (!Number.isFinite(value) || value < 0) return
  apply((previous) => (previous.nominationKw === value ? previous : { ...previous, nominationKw: value }))
}

function Consequence(
  { label, testId, border = false, children }:
  { label: string; testId: string; border?: boolean; children: ReactNode },
): JSX.Element {
  return (
    <div
      data-testid={testId}
      style={{
        padding: '11px 13px',
        ...(border ? { borderRight: `1px solid ${T.color.hairline}` } : {}),
      }}
    >
      <div style={{ ...microLabel, letterSpacing: '0.1em' }}>{label}</div>
      <div style={{ marginTop: 4, fontSize: 22, fontWeight: T.weight.bold }}>{children}</div>
    </div>
  )
}

function Caption({ testId, children }: { testId: string; children: ReactNode }): JSX.Element {
  return (
    <div
      data-testid={testId}
      style={{ fontSize: 11.5, color: T.color.muted, marginTop: 3, fontWeight: T.weight.body }}
    >
      {children}
    </div>
  )
}
